import { VERBOSE } from "../config.js";

export default class ExpandHandler {
  /**
   * @typedef {Object} ExpandHandlerOptions       - Options for the Expand Handler
   * @property {boolean} verbose                  - Call out debug messages
   * @property {HTMLElement} container            - The HTMLElement that is sent into fullscreen
   * @property {'modal'|'fullscreen'} fullscreen  - Use fullscreen-API or HTMLDialogElement
   * @property {string} [classPrefix]             - The prefix for used classnames (will have a dash added between it and classname)
   * @property {boolean} [removeScrolling]        - Should scrolling be removed from the document during fullscreen?
   */

  /**
   * The options for this ExpandHandler
   * @type ExpandHandlerOptions options
   */
  #options;

  /**
   * Prefix (with dash behind it) for classnames
   * @type {string} classPrefix
   */
  #classPrefix = "";

  /**
   * Is the current fullscreen caused by this ExpandHandler?
   * @type {boolean} myFullscreen
   */
  #myFullscreen = false;

  /**
   * The HTMLDialogElement used for fullscreen
   * @type HTMLDialogElement modal
   */
  #modal;

  #placeholder;

  #bodyStyle;

  #isOpen = false;

  #isClosing = false;

  #useFullscreen = false;

  static openEvent = "expandhandler:open";
  static closeEvent = "expandhandler:close";
  static openedEvent = "expandhandler:opened";
  static closedEvent = "expandhandler:closed";

  /**
   * @param {ExpandHandlerOptions} options
   */
  constructor(options) {
    this.#options = { ...options, verbose: options.verbose || VERBOSE };
    this.#classPrefix = options.classPrefix ? options.classPrefix + "-" : "";

    if (
      this.#options.container.requestFullscreen &&
      this.#options.fullscreen !== "modal"
    ) {
      this.#useFullscreen = true;
      document.addEventListener(
        "fullscreenchange",
        this.#fullscreenChangeListener,
      );
    }
  }

  async open() {
    if (this.#options.verbose || VERBOSE) console.log("ExpandHandler: open");
    if (this.#isOpen) return true;

    this.#options.container.dispatchEvent(
      new CustomEvent(ExpandHandler.openEvent),
    );
    this.#options.container.classList.add(this.#classPrefix + "expanded");
    this.#deactivateScroll();

    try {
      if (this.#options.verbose || VERBOSE)
        console.log("ExpandHandler: useFullscreen: ", this.#useFullscreen);
      if (this.#useFullscreen) await this.#openFullscreen();
      else this.#openModal();

      this.#isOpen = true;
      this.#fireOpenedEvent();
      return true;
    } catch (error) {
      this.#restoreModalContainer();
      this.#options.container.classList.remove(this.#classPrefix + "expanded");
      this.#reactivateScroll();
      if (this.#options.verbose || VERBOSE)
        console.warn("ExpandHandler: could not open", error);
      return false;
    }
  }

  async close() {
    if (this.#options.verbose || VERBOSE) console.log("ExpandHandler: close");
    if (!this.#isOpen || this.#isClosing) return false;

    this.#isClosing = true;
    this.#options.container.dispatchEvent(
      new CustomEvent(ExpandHandler.closeEvent),
    );

    try {
      if (this.#useFullscreen) await this.#closeFullscreen();
      else this.#closeModal();
      this.#finishClose();
      return true;
    } catch (error) {
      this.#isClosing = false;
      if (this.#options.verbose || VERBOSE)
        console.warn("ExpandHandler: could not close", error);
      return false;
    }
  }

  #openFullscreen() {
    if (this.#options.verbose || VERBOSE)
      console.log("ExpandHandler: openFullscreen");

    return new Promise((resolve, reject) => {
      const fullscreenChanged = () => {
        if (document.fullscreenElement !== this.#options.container) return;

        document.removeEventListener("fullscreenchange", fullscreenChanged);
        this.#myFullscreen = true;
        if (this.#options.verbose || VERBOSE)
          console.log("ExpandHandler: openFullscreen successful");
        resolve();
      };

      document.addEventListener("fullscreenchange", fullscreenChanged);
      Promise.resolve(this.#options.container.requestFullscreen()).catch(
        (error) => {
          document.removeEventListener("fullscreenchange", fullscreenChanged);
          reject(error);
        },
      );
    });
  }

  #openModal() {
    if (this.#options.verbose || VERBOSE)
      console.log("ExpandHandler: openModal");
    this.#modal = document.createElement("dialog");
    this.#modal.classList.add(this.#classPrefix + "fullscreen-modal");
    this.#placeholder = document.createComment("rhea fullscreen placeholder");
    this.#options.container.before(this.#placeholder);
    this.#options.container.after(this.#modal);
    this.#modal.append(this.#options.container);

    this.#modal.addEventListener("close", this.#modalCloseListener, {
      once: true,
    });

    this.#modal.showModal();
  }

  #closeFullscreen() {
    if (this.#options.verbose || VERBOSE)
      console.log("ExpandHandler: closeFullscreen");

    if (document.fullscreenElement !== this.#options.container)
      return Promise.resolve();

    return document.exitFullscreen().then(() => {
      if (this.#options.verbose || VERBOSE)
        console.log("ExpandHandler: exited fullscreen");
    });
  }

  #closeModal() {
    if (this.#options.verbose || VERBOSE)
      console.log("ExpandHandler: closeModal");

    if (this.#modal) {
      if (this.#modal.open) this.#modal.close();
      this.#restoreModalContainer();
    }
  }

  #deactivateScroll() {
    if (this.#options.verbose || VERBOSE)
      console.log("ExpandHandler: deactivateScroll");

    if (!this.#options.removeScrolling || this.#bodyStyle) return;

    this.#bodyStyle = {
      height: document.body.style.height,
      overflowY: document.body.style.overflowY,
    };
    Object.assign(document.body.style, {
      height: "100dvh",
      overflowY: "hidden",
    });
  }

  #reactivateScroll() {
    if (this.#options.verbose || VERBOSE)
      console.log("ExpandHandler: reactivateScroll");

    if (!this.#bodyStyle) return;

    Object.assign(document.body.style, this.#bodyStyle);
    this.#bodyStyle = undefined;
  }

  #fullscreenChangeListener = () => {
    if (this.#options.verbose || VERBOSE)
      console.log("ExpandHandler: fullscreenChangeListener");

    if (document.fullscreenElement) {
      if (this.#options.verbose || VERBOSE)
        console.log('ExpandHandler: fullscreenChangeListener "open"');
      this.#myFullscreen =
        document.fullscreenElement === this.#options.container;
      if (this.#options.verbose || VERBOSE)
        console.log(
          'ExpandHandler: fullscreenChangeListener "myFullscreen"',
          this.#myFullscreen,
        );
    } else {
      if (this.#options.verbose || VERBOSE)
        console.log('ExpandHandler: fullscreenChangeListener "close"');
      if (this.#options.verbose || VERBOSE)
        console.log(
          'ExpandHandler: fullscreenChangeListener "myFullscreen"',
          this.#myFullscreen,
        );
      if (this.#myFullscreen) {
        this.#myFullscreen = false;
        if (!this.#isClosing) {
          this.#isClosing = true;
          this.#options.container.dispatchEvent(
            new CustomEvent(ExpandHandler.closeEvent),
          );
        }
        this.#finishClose();
      }
    }
  };

  #modalCloseListener = () => {
    if (!this.#isClosing) {
      this.#isClosing = true;
      this.#options.container.dispatchEvent(
        new CustomEvent(ExpandHandler.closeEvent),
      );
    }
    this.#restoreModalContainer();
    this.#finishClose();
  };

  #restoreModalContainer() {
    if (this.#placeholder?.isConnected) {
      this.#placeholder.replaceWith(this.#options.container);
    }
    this.#modal?.remove();
    this.#modal = undefined;
    this.#placeholder = undefined;
  }

  #finishClose() {
    if (!this.#isOpen && !this.#isClosing) return;

    this.#options.container.classList.remove(this.#classPrefix + "expanded");
    this.#reactivateScroll();
    this.#isOpen = false;
    this.#isClosing = false;
    this.#fireClosedEvent();
  }

  destroy() {
    document.removeEventListener(
      "fullscreenchange",
      this.#fullscreenChangeListener,
    );
    this.#restoreModalContainer();
    this.#options.container.classList.remove(this.#classPrefix + "expanded");
    this.#reactivateScroll();
    this.#isOpen = false;
    this.#isClosing = false;
  }

  #fireOpenedEvent() {
    if (this.#options.verbose || VERBOSE)
      console.log("ExpandHandler: fireOpenedEvent");
    this.#options.container.dispatchEvent(
      new CustomEvent(ExpandHandler.openedEvent),
    );
  }

  #fireClosedEvent() {
    if (this.#options.verbose || VERBOSE)
      console.log("ExpandHandler: fireClosedEvent");
    this.#options.container.dispatchEvent(
      new CustomEvent(ExpandHandler.closedEvent),
    );
  }
}
